import React from 'react';

// A single, SO Agent-owned SVG family. These paths are intentionally kept
// local to the authenticated partner portal instead of inheriting the generic
// Lucide or legacy dashboard silhouettes used by the rest of the product.
const ICON_STROKE = 2.1;

function createIcon(name, artwork, { section = false } = {}) {
  const BrandedIcon = React.forwardRef(({
    className = '',
    strokeWidth = ICON_STROKE,
    'aria-label': ariaLabel,
    ...props
  }, ref) => (
    <svg
      ref={ref}
      {...props}
      className={`so-agent-icon${section ? ' so-agent-section-icon' : ''} ${className}`.trim()}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      role={ariaLabel ? 'img' : undefined}
      focusable="false"
      width="24"
      height="24"
      data-so-agent-icon={name}
      data-so-agent-icon-source="studio"
    >
      {artwork}
    </svg>
  ));
  BrandedIcon.displayName = `SoAgent${name.replace(/(^|-)([a-z])/g, (_, __, letter) => letter.toUpperCase())}Icon`;
  return BrandedIcon;
}

const soft = (path) => <path d={path} fill="currentColor" opacity=".12" stroke="none" />;

export const PortalMarkIcon = createIcon('portal-mark', <>
  <path d="M12 2.8 20 7.4v9.2L12 21.2 4 16.6V7.4L12 2.8Z" fill="currentColor" opacity=".13" stroke="none" />
  <path d="M12 2.8 20 7.4v9.2L12 21.2 4 16.6V7.4L12 2.8Z" />
  <path d="m8.4 12 2.2 2.2 5-5" />
</>);

export const HomeSectionIcon = createIcon('section-home', <>
  {soft('M3.5 10.8 12 3.8l8.5 7v9.4h-17Z')}
  <path d="m3.5 10.8 8.5-7 8.5 7v9.4H3.5v-9.4Z" />
  <path d="M9 20.2v-6.5h6v6.5" />
</>, { section: true });

export const CustomersGroupIcon = createIcon('customers', <>
  {soft('M6.4 20v-1.2a5.6 5.6 0 0 1 11.2 0V20Z')}
  <circle cx="12" cy="7.3" r="3" />
  <path d="M6.4 20v-1.2a5.6 5.6 0 0 1 11.2 0V20" />
  <circle cx="5.3" cy="9.2" r="2.1" /><path d="M1.8 19v-.8a4 4 0 0 1 3.5-4M18.7 14.2a4 4 0 0 1 3.5 4v.8" />
  <circle cx="18.7" cy="9.2" r="2.1" />
</>);

export const CustomerChatSectionIcon = createIcon('section-customer-chat', <>
  {soft('M4 5.3h16v11.1H9l-5 4Z')}
  <path d="M4 5.3h16v11.1H9l-5 4v-15Z" />
  <path d="M8 9.2h8M8 12.7h5" />
</>, { section: true });

export const MoreSectionIcon = createIcon('section-more', <>
  <rect x="3.5" y="3.5" width="6.5" height="6.5" rx="1.7" fill="currentColor" opacity=".14" stroke="none" />
  <rect x="3.5" y="3.5" width="6.5" height="6.5" rx="1.7" />
  <rect x="14" y="3.5" width="6.5" height="6.5" rx="1.7" />
  <rect x="3.5" y="14" width="6.5" height="6.5" rx="1.7" />
  <rect x="14" y="14" width="6.5" height="6.5" rx="1.7" fill="currentColor" opacity=".14" stroke="none" />
  <rect x="14" y="14" width="6.5" height="6.5" rx="1.7" />
</>, { section: true });

export const EarningsSectionIcon = createIcon('section-earnings', <>
  {soft('M3 7h18v13H3Z')}
  <circle cx="12" cy="12" r="8.8" />
  <path d="M15.5 8.2h-5a2.1 2.1 0 0 0 0 4.2h3a2.1 2.1 0 0 1 0 4.2H8.3M12 6.2v11.6" />
</>, { section: true });

export const CoinStackIcon = createIcon('coin-stack', <>
  {soft('M4.5 6.4h15v10c0 1.9-3.4 3.4-7.5 3.4s-7.5-1.5-7.5-3.4Z')}
  <ellipse cx="12" cy="6.4" rx="7.5" ry="3.4" />
  <path d="M4.5 6.4v5c0 1.9 3.4 3.4 7.5 3.4s7.5-1.5 7.5-3.4v-5M4.5 11.4v5c0 1.9 3.4 3.4 7.5 3.4s7.5-1.5 7.5-3.4v-5" />
  <path d="M12 4.8v3.1M10.8 5.8h2.4" />
</>);

export const CreditsUsedIcon = createIcon('credits-used', <>
  {soft('M3 10.4A4.4 4.4 0 0 1 7.4 6h9.2a4.4 4.4 0 0 1 4.4 4.4l.7 5.4a2.4 2.4 0 0 1-3.5 2.5l-3.1-1.7H8.9l-3.1 1.7a2.4 2.4 0 0 1-3.5-2.5Z')}
  <path d="M7.4 6h9.2a4.4 4.4 0 0 1 4.4 4.4l.7 5.4a2.4 2.4 0 0 1-3.5 2.5l-3.1-1.7H8.9l-3.1 1.7a2.4 2.4 0 0 1-3.5-2.5l.7-5.4A4.4 4.4 0 0 1 7.4 6Z" />
  <path d="M6.2 11.6h4M8.2 9.6v4M14.8 11.1h.01" />
  <circle cx="17.7" cy="9" r="2.7" fill="currentColor" opacity=".12" stroke="none" />
  <circle cx="17.7" cy="9" r="2.7" /><path d="M16.4 9h2.6" />
</>);

export const GamesSectionIcon = createIcon('section-game-credits', <>
  {soft('M3 10.4A4.4 4.4 0 0 1 7.4 6h9.2a4.4 4.4 0 0 1 4.4 4.4l.7 5.4a2.4 2.4 0 0 1-3.5 2.5l-3.1-1.7H8.9l-3.1 1.7a2.4 2.4 0 0 1-3.5-2.5Z')}
  <path d="M7.4 6h9.2a4.4 4.4 0 0 1 4.4 4.4l.7 5.4a2.4 2.4 0 0 1-3.5 2.5l-3.1-1.7H8.9l-3.1 1.7a2.4 2.4 0 0 1-3.5-2.5l.7-5.4A4.4 4.4 0 0 1 7.4 6Z" />
  <path d="M6.2 11.6h4M8.2 9.6v4M14.8 11.1h.01" />
  <circle cx="17.7" cy="9" r="2.7" fill="currentColor" opacity=".12" stroke="none" />
  <circle cx="17.7" cy="9" r="2.7" /><path d="M16.4 9h2.6" />
</>, { section: true });

export const InviteAgentIcon = createIcon('invite-agent', <>
  {soft('M3.5 20v-1.5A5.5 5.5 0 0 1 9 13a5.5 5.5 0 0 1 5.5 5.5V20Z')}
  <circle cx="9" cy="8" r="3" />
  <path d="M3.5 20v-1.5A5.5 5.5 0 0 1 9 13a5.5 5.5 0 0 1 5.5 5.5V20M18 8v7M14.5 11.5h7" />
</>);

export const NewJoinedIcon = createIcon('new-joined-customer', <>
  {soft('M3.5 20v-1.4A5.5 5.5 0 0 1 9 13.1a5.5 5.5 0 0 1 5.5 5.5V20Z')}
  <circle cx="9" cy="8.2" r="3" />
  <path d="M3.5 20v-1.4A5.5 5.5 0 0 1 9 13.1a5.5 5.5 0 0 1 5.5 5.5V20" />
  <path d="m15.3 11.8 2.1-2.1 1.7 1.6 2.2-3" />
  <path d="M18.8 8.3h2.5v2.5" />
</>);

export const ReportsSectionIcon = createIcon('section-reports', <>
  {soft('M5 3.5h9.4L19 8.1v12.4H5Z')}
  <path d="M6 3.5h8.4L19 8.1v12.4H6a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" />
  <path d="M14.4 3.5v4.6H19M7.5 16.3l2.5-2.5 2 1.8 3.8-4M14.2 11.6h1.6v1.6" />
</>, { section: true });

export const AnalyticsSectionIcon = createIcon('section-analytics', <>
  {soft('M4 4h16v16H4Z')}
  <path d="M4 19.5V5M4 19.5h16" />
  <path d="M7 17v-4M11.2 17V9.8M15.4 17V6.5" />
  <path d="m7 10.2 4.2-3.1 3.1 1.5 4-4M15.8 4.6h2.5v2.5" />
</>, { section: true });

export const TransparentLedgerIcon = createIcon('transparent-ledger', <>
  {soft('M5 3.5h12a2 2 0 0 1 2 2v15H7a2 2 0 0 1-2-2Z')}
  <path d="M5 3.5h12a2 2 0 0 1 2 2v15H7a2 2 0 0 1-2-2v-15Z" />
  <path d="M8 7.5h8M8 11.5h8M8 15.5h4M5 18.5a2 2 0 0 1 2-2h12" />
  <path d="m14.2 15.3 1.3 1.3 2.6-2.8" />
</>);

export const WithdrawActionIcon = createIcon('withdraw-action', <>
  {soft('M4 6.4h14v13.6H6.4A2.4 2.4 0 0 1 4 17.6Z')}
  <path d="M4 6.4A2.4 2.4 0 0 1 6.4 4H18v16H6.4A2.4 2.4 0 0 1 4 17.6V6.4Z" />
  <path d="M14 9h7v6h-7a3 3 0 1 1 0-6Z" />
  <circle cx="15" cy="12" r=".7" fill="currentColor" stroke="none" />
  <path d="M9 10.5v5M7.2 13.7 9 15.5l1.8-1.8" />
</>);

export const WalletBalanceIcon = createIcon('wallet-balance', <>
  {soft('M4 6.4h14v13.6H6.4A2.4 2.4 0 0 1 4 17.6Z')}
  <path d="M4 6.4A2.4 2.4 0 0 1 6.4 4H18v16H6.4A2.4 2.4 0 0 1 4 17.6V6.4Z" />
  <path d="M14 9h7v6h-7a3 3 0 1 1 0-6Z" />
  <circle cx="15" cy="12" r=".7" fill="currentColor" stroke="none" />
</>);

export const WithdrawalHistoryIcon = createIcon('withdrawal-history', <>
  {soft('M4 6.4h14v13.6H6.4A2.4 2.4 0 0 1 4 17.6Z')}
  <path d="M4 6.4A2.4 2.4 0 0 1 6.4 4H18v16H6.4A2.4 2.4 0 0 1 4 17.6V6.4Z" />
  <path d="M14 9h7v6h-7a3 3 0 1 1 0-6Z" />
  <path d="M19.3 18.2a3.2 3.2 0 0 1-5.5 1.2M13.8 19.4v-2.1h2.1" />
</>);

export const MoneyFlowIcon = createIcon('money-flow', <>
  {soft('M3 6h18v12H3Z')}
  <circle cx="12" cy="12" r="7.7" />
  <path d="M14.4 9.7c-.4-.8-1.2-1.3-2.4-1.3-1.4 0-2.4.7-2.4 1.8 0 2.7 4.8 1.2 4.8 3.8 0 1.1-1 1.8-2.4 1.8-1.2 0-2.1-.5-2.6-1.4M12 7v10" />
  <path d="m4.3 10-1.5 1.5L4.3 13M19.7 11l1.5 1.5-1.5 1.5" />
</>);

export const ExpensesReceiptIcon = createIcon('expenses-receipt', <>
  {soft('M6 3.5h12v17l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3-2 1.3Z')}
  <path d="M6 3.5h12v17l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3-2 1.3v-17Z" />
  <path d="M8.5 8h5M8.5 11h3M10.2 17l4.5-4.5" />
  <circle cx="10.2" cy="13" r=".8" /><circle cx="14.7" cy="16.5" r=".8" />
</>);

export const ReferralLinkIcon = createIcon('customer-link', <>
  {soft('M4 8.5h6v7H4ZM14 8.5h6v7h-6Z')}
  <path d="m9.8 14.2 4.4-4.4M7.1 16.9H5.8a4 4 0 0 1 0-8h3.7M16.9 7.1h1.3a4 4 0 1 1 0 8h-3.7" />
</>);

export const ReferralTreeIcon = createIcon('referral-tree', <>
  {soft('M9 3h6v6H9ZM2.5 15h6v6h-6ZM15.5 15h6v6h-6Z')}
  <circle cx="12" cy="5.5" r="2.3" />
  <circle cx="5.5" cy="18" r="2.3" /><circle cx="18.5" cy="18" r="2.3" />
  <path d="M12 7.8v5.8M5.5 15.7v-2.1h13v2.1" />
</>);

export const TeamChatIcon = createIcon('team-chat', <>
  {soft('M3 5h14v11H8l-5 4Z')}
  <path d="M4.5 5.2h10A2.5 2.5 0 0 1 17 7.7v5.1a2.5 2.5 0 0 1-2.5 2.5H9l-3.7 2.9.7-2.9H4.5A2.5 2.5 0 0 1 2 12.8V7.7a2.5 2.5 0 0 1 2.5-2.5Z" />
  <path d="M17.2 9.5h1.3A2.5 2.5 0 0 1 21 12v4.3a2.5 2.5 0 0 1-2.5 2.5h-1l.5 2-2.7-2h-2.1M6.5 10.2h.01M9.5 10.2h.01M12.5 10.2h.01" />
</>);

export const HowItWorksIcon = createIcon('how-it-works', <>
  {soft('M4 3h7v16H6.7A2.7 2.7 0 0 0 4 21.7ZM13 3h4.3A2.7 2.7 0 0 1 20 5.7v16a2.7 2.7 0 0 0-2.7-2.7H13Z')}
  <path d="M4 5.7A2.7 2.7 0 0 1 6.7 3H11v16H6.7A2.7 2.7 0 0 0 4 21.7V5.7ZM20 5.7A2.7 2.7 0 0 0 17.3 3H13v16h4.3a2.7 2.7 0 0 1 2.7 2.7V5.7Z" />
  <path d="M7 8h1.4M7 11h1.4" />
  <circle cx="15.5" cy="7.2" r=".8" /><circle cx="17.5" cy="10.2" r=".8" /><circle cx="15.5" cy="13.2" r=".8" />
  <path d="m16 7.8 1 1.7-1 2.8" />
</>);

export const ProfileSectionIcon = createIcon('section-profile', <>
  {soft('M4 21a8 8 0 0 1 16 0Z')}
  <circle cx="12" cy="8" r="4" />
  <path d="M4 21a8 8 0 0 1 16 0" />
</>, { section: true });

export const SettingsSectionIcon = createIcon('section-settings', <>
  {soft('M4 5h16v4H4ZM4 15h16v4H4Z')}
  <path d="M3 6.8h18M3 17.2h18" />
  <circle cx="8" cy="6.8" r="2.5" /><circle cx="16" cy="17.2" r="2.5" />
</>, { section: true });

// Utility glyphs use the same 24px geometry and 2.1px optical weight as the
// business icons. They are deliberately local rather than aliases to Lucide.
export const ArrowDownRight = createIcon('arrow-down-right', <><path d="M6 6 18 18" /><path d="M10 18h8v-8" /></>);
export const ArrowLeft = createIcon('arrow-left', <><path d="M19 12H5M10 7l-5 5 5 5" /></>);
export const ArrowUpRight = createIcon('arrow-up-right', <><path d="M5 19 19 5M11 5h8v8" /></>);
export const ChevronRight = createIcon('chevron-right', <path d="m9 5 7 7-7 7" />);
export const ChevronDown = createIcon('chevron-down', <path d="m5 9 7 7 7-7" />);
export const Bell = createIcon('notifications', <><path d="M18.5 10.2A6.5 6.5 0 0 0 5.5 10.2c0 7-2.5 7-2.5 7h18s-2.5 0-2.5-7Z" /><path d="M9.5 20a2.8 2.8 0 0 0 5 0" /></>);
export const CalendarDays = createIcon('calendar', <><rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M7.5 2.8v4.4M16.5 2.8v4.4M3.5 9.5h17" /></>);
export const Camera = createIcon('camera', <><path d="M4 8h3l1.5-2.5h7L17 8h3v11H4V8Z" /><circle cx="12" cy="13" r="3.5" /></>);
export const CheckCircle2 = createIcon('verified', <><circle cx="12" cy="12" r="9" fill="currentColor" opacity=".1" stroke="none" /><circle cx="12" cy="12" r="9" /><path d="m7.7 12.3 2.8 2.8 5.9-6" /></>);
export const Copy = createIcon('copy', <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></>);
export const Download = createIcon('download', <path d="M12 3v12M7 10l5 5 5-5M4 20h16" />);
export const Eye = createIcon('view', <><path d="M2.8 12s3.5-6 9.2-6 9.2 6 9.2 6-3.5 6-9.2 6-9.2-6-9.2-6Z" /><circle cx="12" cy="12" r="2.8" /></>);
export const EyeOff = createIcon('hide', <><path d="M4 4 20 20M10.4 6.2c.5-.1 1-.2 1.6-.2 5.7 0 9.2 6 9.2 6a16 16 0 0 1-2.2 2.8M7.1 7.3A16.4 16.4 0 0 0 2.8 12s3.5 6 9.2 6c1.2 0 2.3-.3 3.3-.7M9.8 9.8a3.1 3.1 0 0 0 4.4 4.4" /></>);
export const ExternalLink = createIcon('external-link', <><path d="M14 4h6v6M20 4l-9 9" /><path d="M18 13v6a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5h6" /></>);
export const Filter = createIcon('filter', <path d="M3 5h18l-7 8v6l-4 2v-8L3 5Z" />);
export const Gamepad2 = createIcon('games', <><path d="M7.2 8.4h9.6c2 0 3.2 1 3.7 3.2l.8 3.5c.4 1.8-1 3.1-2.7 2.5l-2.2-.8H7.6l-2.2.8c-1.7.6-3.1-.7-2.7-2.5l.8-3.5c.5-2.2 1.7-3.2 3.7-3.2Z" fill="currentColor" opacity=".1" stroke="none" /><path d="M7.2 8.4h9.6c2 0 3.2 1 3.7 3.2l.8 3.5c.4 1.8-1 3.1-2.7 2.5l-2.2-.8H7.6l-2.2.8c-1.7.6-3.1-.7-2.7-2.5l.8-3.5c.5-2.2 1.7-3.2 3.7-3.2ZM7 12.5v3M5.5 14h3M16.5 12.8h.01M18.5 14.7h.01" /></>);
export const Headphones = createIcon('support', <><path d="M4 13v-1a8 8 0 0 1 16 0v1" /><path d="M4 12h2.5v6H5.8A1.8 1.8 0 0 1 4 16.2ZM20 12h-2.5v6h.7a1.8 1.8 0 0 0 1.8-1.8Z" /><path d="M17.5 18c-.8 2-2.4 3-4.8 3" /></>);
export const LifeBuoy = createIcon('help', <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3.2" /><path d="m6 6 3.7 3.7M14.3 14.3 18 18M18 6l-3.7 3.7M9.7 14.3 6 18" /></>);
export const LockKeyhole = createIcon('secure', <><rect x="4.5" y="10" width="15" height="11" rx="2" /><path d="M8 10V7.5a4 4 0 0 1 8 0V10M12 14v3" /></>);
export const Mail = createIcon('email', <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></>);
export const MessageCircle = createIcon('customer-chat', <><path d="M20.5 14.5a8.5 8.5 0 1 0-4 4L22 20l-1.5-5.5Z" fill="currentColor" opacity=".09" stroke="none" /><path d="M20.5 14.5a8.5 8.5 0 1 0-4 4L22 20l-1.5-5.5ZM7.5 10h9M7.5 14h5.5" /></>);
export const MessageSquareText = createIcon('team-message', <><path d="M4 5.3h16v11.1H9l-5 4v-15Z" fill="currentColor" opacity=".09" stroke="none" /><path d="M4 5.3h16v11.1H9l-5 4v-15ZM8 9.2h8M8 12.7h5" /></>);
export const Moon = createIcon('dark-mode', <path d="M20.2 15.6A8.4 8.4 0 0 1 8.4 3.8a8.4 8.4 0 1 0 11.8 11.8Z" />);
export const Paperclip = createIcon('attachment', <path d="m9 13.5 5.8-5.8a3.2 3.2 0 0 1 4.5 4.5l-7.9 7.9a5 5 0 0 1-7.1-7.1l8-8a2.8 2.8 0 1 1 4 4l-7.8 7.8" />);
export const Percent = createIcon('percentage', <><path d="m6 18 12-12" /><circle cx="7" cy="7" r="2.2" /><circle cx="17" cy="17" r="2.2" /></>);
export const Photo = createIcon('photo', <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="m4 17 5-5 3.5 3.5 2.5-2.5 5 5" /></>);
export const Plus = createIcon('plus', <path d="M12 4v16M4 12h16" />);
export const RefreshCw = createIcon('refresh', <><path d="M20 7v5h-5" /><path d="M18.2 16.5a8 8 0 1 1 .9-8.1L20 12" /></>);
export const Save = createIcon('save', <><path d="M4 3.5h13l3 3V20H4Z" /><path d="M8 3.5v6h8v-6M8 20v-6h8v6" /></>);
export const Search = createIcon('search', <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.4 15.4 5.1 5.1" /></>);
export const Send = createIcon('send', <><path d="m3 4 18 8-18 8 3.2-8L3 4Z" fill="currentColor" opacity=".1" stroke="none" /><path d="m3 4 18 8-18 8 3.2-8L3 4ZM6.2 12H21" /></>);
export const Settings = createIcon('settings', <><path d="M3 6.8h18M3 17.2h18" /><circle cx="8" cy="6.8" r="2.5" /><circle cx="16" cy="17.2" r="2.5" /></>);
export const ShieldCheck = createIcon('protected', <><path d="M12 2.8 20 6v5.8c0 5-3.3 8.1-8 9.5-4.7-1.4-8-4.5-8-9.5V6l8-3.2Z" fill="currentColor" opacity=".1" stroke="none" /><path d="M12 2.8 20 6v5.8c0 5-3.3 8.1-8 9.5-4.7-1.4-8-4.5-8-9.5V6l8-3.2ZM8.5 12l2.2 2.2 4.8-4.8" /></>);
export const Smartphone = createIcon('phone', <><rect x="7" y="2.5" width="10" height="19" rx="2" /><path d="M10.5 5h3M11.5 18.5h1" /></>);
export const Sparkles = createIcon('welcome', <><path d="m12 2 1.4 4.1L17.5 7.5l-4.1 1.4L12 13l-1.4-4.1-4.1-1.4 4.1-1.4L12 2Z" /><path d="m18.5 14 .8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" /></>);
export const Sun = createIcon('light-mode', <><circle cx="12" cy="12" r="4" /><path d="M12 2v2.2M12 19.8V22M2 12h2.2M19.8 12H22M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M19.1 4.9l-1.6 1.6M6.5 17.5l-1.6 1.6" /></>);
export const Trash2 = createIcon('remove', <><path d="M4 7h16M9 3h6l1 4H8l1-4ZM6 7l1 14h10l1-14M10 11v6M14 11v6" /></>);
export const TrendingUp = createIcon('trending-up', <><path d="m4 16 5-5 3.5 3.5L20 7" /><path d="M14 7h6v6" /></>);
export const UserCog = createIcon('manager', <><circle cx="9" cy="8" r="3" /><path d="M3.5 20v-1.5A5.5 5.5 0 0 1 9 13c1.4 0 2.6.4 3.5 1.1" /><circle cx="18" cy="17" r="3" /><path d="M18 12.8v1.2M18 20v1.2M13.8 17H15M21 17h1.2" /></>);
export const UserRound = createIcon('profile', <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>);
export const X = createIcon('close', <path d="M5 5l14 14M19 5 5 19" />);
export const Clock = createIcon('clock', <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>);

// Semantic aliases keep every existing call site stable while ensuring the
// rendered silhouette comes from this branded family.
export const Home = HomeSectionIcon;
export const Users = CustomersGroupIcon;
export const UserPlus = InviteAgentIcon;
export const Wallet = WalletBalanceIcon;
export const CircleDollarSign = EarningsSectionIcon;
export const BadgeDollarSign = EarningsSectionIcon;
export const BarChart3 = AnalyticsSectionIcon;
export const LineChart = AnalyticsSectionIcon;
export const GitBranch = ReferralTreeIcon;
export const Network = ReferralTreeIcon;
export const Grid2X2 = MoreSectionIcon;
export const Link2 = ReferralLinkIcon;
export const ScrollText = TransparentLedgerIcon;
export const LogOut = createIcon('logout', <><path d="M10 4H5v16h5M14 8l4 4-4 4M18 12H9" /></>);
export const Code2 = createIcon('development', <path d="m8 7-5 5 5 5M16 7l5 5-5 5M14 4l-4 16" />);

export const ActiveCustomerIcon = createIcon('active-customer', <>
  <circle cx="9" cy="8" r="3" /><path d="M3.5 20v-1.4A5.5 5.5 0 0 1 9 13.1c1.6 0 2.9.5 3.9 1.4" />
  <circle cx="18" cy="17" r="3.2" fill="currentColor" opacity=".1" stroke="none" /><circle cx="18" cy="17" r="3.2" /><path d="m16.6 17 .9.9 1.9-2" />
</>);
export const AnalyticsChartIcon = AnalyticsSectionIcon;
export const ReportsDocumentIcon = ReportsSectionIcon;

export function IconTile({ icon: Icon, tone = 'green', className = '', ...props }) {
  return (
    <span {...props} data-tone={tone} className={`agent-dashboard-icon-tile tone-${tone} ${className}`.trim()}>
      <Icon />
    </span>
  );
}

export const portalIconTokens = Object.freeze({
  viewBox: 24,
  strokeWidth: ICON_STROKE,
  menuSize: 28,
  metricSize: 30,
  actionSize: 33,
  chevronSize: 23,
});
